// material/view-model — pure projections of a Shown focus into what the side
// view renders: the ordered part rows, the job controls, and the header's job
// status. Total, no I/O. The view only maps these to words and elements.
//
// Rows: the skeleton gives the reading order and each part's pre-job status;
// the job snapshot (when there is one) overrides the status of the parts it
// covers (a job may cover only some), and parts it knows that the skeleton
// does not are appended. Without a skeleton read, the job's own order stands.

import { match, type NewType } from "@bilda/utils";
import type {
    GenerationJobSnapshot,
    GenerationPartSnapshot,
    NewerVersions,
    PartCheck,
    PartReview,
    SkeletonPart,
    StaleReason,
} from "../../app/ports.js";
import type { MaterialRef } from "../../components/Navigation/types.js";
import {
    contentKey,
    type JobFeed,
    type Shown,
    type StillFine,
} from "./model.js";

export type RowStatus =
    | "planned"
    | "written"
    | "removed"
    | "queued"
    | "running"
    | "landed"
    | "failed"
    | "cancelled"
    | "kept";

// Why a part failed, in the author's terms (the server's reason is
// developer text and never shown).
export type FailureKind = "unfinished" | "not_saved" | "no_answer";

// What a landed part was checked against (design v2.1 §4): how many checks
// ran per evaluator, how many need attention, and the items, those needing
// attention first.
export type PartChecks = {
    readonly computed: number;
    readonly judged: number;
    readonly attention: number;
    readonly items: readonly PartCheck[];
};

export const partChecks = (
    checks: readonly PartCheck[] | null,
): PartChecks | null => {
    if (checks === null || checks.length === 0) return null;
    const needs = checks.filter((c) => c.outcome === "needs_attention");
    return {
        computed: checks.filter((c) => c.evaluator === "computed").length,
        judged: checks.filter((c) => c.evaluator === "judged").length,
        attention: needs.length,
        items: [...needs, ...checks.filter((c) => c.outcome === "passed")],
    };
};

export type PartRow = {
    readonly id: string;
    readonly title: string;
    readonly status: RowStatus;
    readonly failure: FailureKind | null;
    // Only a part the job landed carries checks.
    readonly checks: PartChecks | null;
    // The content to open (`contentKey`); null = nothing to read yet.
    readonly contentKey: string | null;
    readonly expanded: boolean;
    // "Ge feedback" is offered (a landed part of a job that can take it).
    readonly canRevise: boolean;
    readonly revising: boolean;
    // Phase 3. What the part builds on; `ref` = openable in the panel (a
    // dependency that is a part of this material; other kinds are named only,
    // the view does not say what they are).
    readonly buildsOn: readonly Dependency[];
    // The dependencies that changed since the part was written (empty =
    // fresh, still being written, or answered "still fine").
    readonly stale: readonly StaleReason[];
    readonly stillFine: StillFine | null;
    // "Regenerate" is offered now (no job running, nothing starting).
    readonly canRegenerate: boolean;
    // The include pins an older version than the part's head(s); null once
    // the author answered (use a version, or keep this one).
    readonly newerVersions: NewerVersions | null;
    // Phase 5a: the author's review of the part's newest version, with an
    // in-flight approve / revoke already applied (optimistic). Null = nothing
    // to review (planned, being written, removed, or an older server).
    readonly review: PartReview["status"] | null;
    // "Approve" is offered: the version it approves (the newest), else null.
    readonly approveVersion: number | null;
    // "Undo approval" is offered: the approved version, else null.
    readonly revokeVersion: number | null;
};

type Dependency = {
    readonly id: string;
    readonly title: string;
    readonly ref: MaterialRef | null;
};

export type JobControls =
    | NewType<"Controls.None">
    | NewType<
          "Controls.Generate",
          {
              readonly remaining: number;
              // Some parts are already written: "generate the rest".
              readonly resume: boolean;
              readonly pending: boolean;
          }
      >
    | NewType<"Controls.Running", { readonly stopping: boolean }>
    // A server restart cut the job short; Continue writes what is left.
    | NewType<
          "Controls.Interrupted",
          { readonly remaining: number; readonly pending: boolean }
      >
    | NewType<
          "Controls.Paused",
          { readonly remaining: number; readonly pending: boolean }
      >
    | NewType<"Controls.Waiting">;

export type JobStatus =
    | NewType<"Status.Loading">
    | NewType<"Status.Reconnecting">
    | NewType<"Status.Planned", { readonly planned: number }>
    | NewType<"Status.Written", { readonly parts: number }>
    // No facts were saved from the sources yet: the job reads them first.
    | NewType<"Status.Reading">
    // That read saved no fact, so nothing was written.
    | NewType<"Status.ReadFailed", { readonly reason: ReadFailure }>
    | NewType<
          "Status.Running",
          { readonly landed: number; readonly total: number }
      >
    | NewType<"Status.Stopping">
    | NewType<"Status.Paused", { readonly remaining: number }>
    | NewType<
          "Status.Interrupted",
          { readonly landed: number; readonly total: number }
      >
    | NewType<
          "Status.Stopped",
          { readonly landed: number; readonly total: number }
      >
    | NewType<"Status.Done", { readonly landed: number }>;

// The snapshot the feed currently shows, if any.
export const snapshotOf = (feed: JobFeed): GenerationJobSnapshot | null =>
    match<JobFeed, GenerationJobSnapshot | null>({
        "JobFeed.Loading": () => null,
        "JobFeed.NoJob": () => null,
        "JobFeed.Live": ({ snapshot }) => snapshot,
        "JobFeed.Lost": ({ last }) => last,
    })(feed);

// An interrupted job has no live process behind it: nothing runs.
const isTerminal = (s: GenerationJobSnapshot): boolean =>
    s.phase === "stopped" ||
    s.phase === "done" ||
    s.phase === "read_failed" ||
    s.phase === "interrupted";

// Why the read saved no fact (`unknown`: a reason this client does not know).
export type ReadFailure =
    | NonNullable<GenerationJobSnapshot["readFailure"]>
    | "unknown";

// `discarded` = the author changed the part meanwhile; their version is kept.
const jobStatus = (state: GenerationPartSnapshot["state"]): RowStatus =>
    state === "discarded" ? "kept" : state;

const failureKind = (reason: string | null): FailureKind => {
    if (reason === null) return "no_answer";
    if (reason.startsWith("still has issues")) return "unfinished";
    if (reason.startsWith("landing refused")) return "not_saved";
    return "no_answer";
};

const keyFor = (
    id: string,
    status: RowStatus,
    version: number | null,
): string | null => {
    switch (status) {
        case "landed":
        case "kept":
            return version === null ? null : contentKey(id, version);
        case "written":
            return contentKey(id, "head");
        default:
            return null;
    }
};

type RowFacts = {
    readonly id: string;
    readonly title: string;
    readonly status: RowStatus;
    readonly version: number | null;
    readonly reason: string | null;
    readonly checks: readonly PartCheck[] | null;
};

const fromSkeleton = (p: SkeletonPart): RowFacts => ({
    id: p.id,
    title: p.title,
    status: p.status,
    version: null,
    reason: null,
    checks: null,
});

const fromJob = (p: GenerationPartSnapshot): RowFacts => ({
    id: p.id,
    title: p.title,
    status: jobStatus(p.state),
    version: p.version,
    reason: p.reason,
    checks: p.checks,
});

const mergeFacts = (
    skeleton: readonly SkeletonPart[],
    job: GenerationJobSnapshot | null,
): readonly RowFacts[] => {
    const jobParts = job?.parts ?? [];
    const byId = new Map(jobParts.map((p) => [p.id, p]));
    const known = new Set(skeleton.map((p) => p.id));
    return [
        ...skeleton.map((p) => {
            const j = byId.get(p.id);
            return j === undefined ? fromSkeleton(p) : fromJob(j);
        }),
        ...jobParts.filter((p) => !known.has(p.id)).map(fromJob),
    ];
};

const skeletonParts = (focus: Shown): readonly SkeletonPart[] =>
    focus.skeleton._tag === "Remote.Loaded" ? focus.skeleton.value.parts : [];

// A start is possible: the job is known, none is active, and no intent is in
// flight (the server refuses a start while a job for the root is active).
const startOpen = (focus: Shown): boolean => {
    if (focus.pending !== "idle") return false;
    if (focus.feed._tag === "JobFeed.NoJob") return true;
    return (
        focus.feed._tag === "JobFeed.Live" && isTerminal(focus.feed.snapshot)
    );
};

// Outdated is only said of content that exists: a part still to be written,
// or being written, will read its dependencies afresh anyway.
const HAS_CONTENT: ReadonlySet<RowStatus> = new Set([
    "written",
    "landed",
    "kept",
]);

type RowReview = Pick<PartRow, "review" | "approveVersion" | "revokeVersion">;

const noReview: RowReview = {
    review: null,
    approveVersion: null,
    revokeVersion: null,
};

const rowReview = (
    focus: Shown,
    id: string,
    status: RowStatus,
    review: PartReview | null,
): RowReview => {
    if (review === null || !HAS_CONTENT.has(status)) return noReview;
    const inFlight = focus.reviewing.get(id);
    if (inFlight !== undefined)
        return {
            ...noReview,
            review:
                inFlight.intent === "Intent.Approve"
                    ? "reviewed"
                    : "unreviewed",
        };
    return {
        review: review.status,
        approveVersion: review.status === "reviewed" ? null : review.head,
        revokeVersion:
            review.status === "unreviewed"
                ? null
                : (review.approved ?? review.head),
    };
};

export const partRows = (focus: Shown): readonly PartRow[] => {
    const job = snapshotOf(focus.feed);
    // The server takes feedback while the job runs, waits, or is done (a done
    // job reopens); a stopping, stopped or interrupted job refuses it.
    const reviseOpen =
        focus.feed._tag === "JobFeed.Live" &&
        focus.feed.snapshot.phase !== "stopping" &&
        focus.feed.snapshot.phase !== "stopped" &&
        focus.feed.snapshot.phase !== "interrupted";
    const skeleton = skeletonParts(focus);
    const byId = new Map(skeleton.map((p) => [p.id, p]));
    const regenerateOpen = startOpen(focus);
    return mergeFacts(skeleton, job).map((f) => {
        const sk = byId.get(f.id);
        const stillFine = focus.stillFine.get(f.id) ?? null;
        const stale =
            sk !== undefined && stillFine === null && HAS_CONTENT.has(f.status)
                ? sk.stale
                : [];
        return {
            id: f.id,
            title: f.title,
            status: f.status,
            failure: f.status === "failed" ? failureKind(f.reason) : null,
            checks: f.status === "landed" ? partChecks(f.checks) : null,
            contentKey: keyFor(f.id, f.status, f.version),
            expanded: focus.expanded.has(f.id),
            canRevise: reviseOpen && f.status === "landed",
            revising: focus.revising.has(f.id),
            buildsOn: (sk?.buildsOn ?? []).map((d) => ({
                id: d.id,
                title: d.title,
                ref: byId.has(d.id)
                    ? { type: "material", id: d.id, title: d.title }
                    : null,
            })),
            stale,
            stillFine,
            canRegenerate: regenerateOpen && stale.length > 0,
            newerVersions: focus.pinAnswers.has(f.id)
                ? null
                : (sk?.newerVersions ?? null),
            ...rowReview(focus, f.id, f.status, sk?.review ?? null),
        };
    });
};

// The header's review progress ("3 of 5 parts reviewed"): shown once any
// part has content to review.
export type ReviewProgress = {
    readonly reviewed: number;
    readonly total: number;
};

export const reviewProgress = (focus: Shown): ReviewProgress | null => {
    const rows = partRows(focus).filter((r) => r.status !== "removed");
    return rows.some((r) => r.review !== null)
        ? {
              reviewed: rows.filter((r) => r.review === "reviewed").length,
              total: rows.length,
          }
        : null;
};

// The header's summary: which parts may need updating, and whether
// "Regenerate all outdated" is offered now. Null = nothing is outdated.
export type StaleSummary = {
    readonly parts: readonly string[];
    readonly canRegenerate: boolean;
};

export const staleSummary = (focus: Shown): StaleSummary | null => {
    const parts = partRows(focus)
        .filter((r) => r.stale.length > 0)
        .map((r) => r.id);
    return parts.length === 0
        ? null
        : { parts, canRegenerate: startOpen(focus) };
};

// Parts a fresh start would generate: still placeholders (or left over by a
// stopped job). Matches the server's default plan scope.
const STILL_TO_WRITE: ReadonlySet<RowStatus> = new Set([
    "planned",
    "queued",
    "cancelled",
    "failed",
]);
const WRITTEN: ReadonlySet<RowStatus> = new Set(["written", "landed", "kept"]);

const remainingNotice = (s: GenerationJobSnapshot): number | undefined =>
    s.notices.flatMap((n) =>
        n.type === "generation_notice_ask_to_continue"
            ? [n.remaining.length]
            : [],
    )[0];

const landedNotice = (s: GenerationJobSnapshot): number | undefined =>
    s.notices.flatMap((n) =>
        n.type === "generation_notice_end_summary" ? [n.landed.length] : [],
    )[0];

const countLanded = (s: GenerationJobSnapshot): number =>
    s.parts.filter((p) => p.state === "landed").length;

const countUnfinished = (s: GenerationJobSnapshot): number =>
    s.parts.filter((p) => p.state !== "landed" && p.state !== "discarded")
        .length;

export const jobControls = (focus: Shown): JobControls => {
    if (
        focus.feed._tag === "JobFeed.Loading" ||
        focus.feed._tag === "JobFeed.Lost"
    )
        return { _tag: "Controls.Waiting" };
    const job = snapshotOf(focus.feed);
    if (job !== null && job.phase === "paused_budget")
        return {
            _tag: "Controls.Paused",
            remaining: remainingNotice(job) ?? countUnfinished(job),
            pending: focus.pending === "continuing",
        };
    if (job !== null && job.phase === "interrupted" && countUnfinished(job) > 0)
        return {
            _tag: "Controls.Interrupted",
            remaining: countUnfinished(job),
            pending: focus.pending === "continuing",
        };
    if (job !== null && !isTerminal(job))
        return {
            _tag: "Controls.Running",
            stopping: job.phase === "stopping" || focus.pending === "stopping",
        };
    const rows = partRows(focus);
    const remaining = rows.filter((r) => STILL_TO_WRITE.has(r.status)).length;
    if (remaining === 0 && focus.pending !== "starting")
        return { _tag: "Controls.None" };
    return {
        _tag: "Controls.Generate",
        remaining,
        resume: rows.some((r) => WRITTEN.has(r.status)),
        pending: focus.pending === "starting",
    };
};

export const jobStatusOf = (focus: Shown): JobStatus => {
    if (focus.feed._tag === "JobFeed.Loading")
        return { _tag: "Status.Loading" };
    if (focus.feed._tag === "JobFeed.Lost")
        return { _tag: "Status.Reconnecting" };
    const job = snapshotOf(focus.feed);
    if (job === null) {
        const rows = partRows(focus);
        const planned = rows.filter((r) => r.status === "planned").length;
        return planned > 0
            ? { _tag: "Status.Planned", planned }
            : { _tag: "Status.Written", parts: rows.length };
    }
    switch (job.phase) {
        case "reading":
            return { _tag: "Status.Reading" };
        case "read_failed":
            return {
                _tag: "Status.ReadFailed",
                reason: job.readFailure ?? "unknown",
            };
        case "running":
            return {
                _tag: "Status.Running",
                landed: countLanded(job),
                total: job.parts.length,
            };
        case "stopping":
            return { _tag: "Status.Stopping" };
        case "paused_budget":
            return {
                _tag: "Status.Paused",
                remaining: remainingNotice(job) ?? countUnfinished(job),
            };
        case "stopped":
            return {
                _tag: "Status.Stopped",
                landed: countLanded(job),
                total: job.parts.length,
            };
        case "interrupted":
            return {
                _tag: "Status.Interrupted",
                landed: countLanded(job),
                total: job.parts.length,
            };
        case "done":
            return {
                _tag: "Status.Done",
                landed: landedNotice(job) ?? countLanded(job),
            };
    }
};

// Whether this focus is a generation at all (has parts or a job): the side
// view shows only then; a plain material keeps its usual panel.
export const isGeneration = (focus: Shown): boolean =>
    skeletonParts(focus).length > 0 || snapshotOf(focus.feed) !== null;

// Any part written by the job (the header's AI marker).
export const hasAiParts = (focus: Shown): boolean =>
    partRows(focus).some((r) => r.status === "landed");
